import {DuplicateIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {commitUnmarkIssueAsDuplicateMutation} from '../mutations/unmark-issue-as-duplicate-mutation'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {MarkedAsDuplicateEvent$key} from './__generated__/MarkedAsDuplicateEvent.graphql'
import {IssueLink} from './IssueLink'
import {TimelineRow} from './row/TimelineRow'

type MarkedAsDuplicateEventProps = {
  queryRef: MarkedAsDuplicateEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  currentIssueId: string
  repositoryId: string
  ownerLogin: string
}

export function MarkedAsDuplicateEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  currentIssueId,
  repositoryId,
  ownerLogin,
}: MarkedAsDuplicateEventProps): React.ReactElement {
  const environment = useRelayEnvironment()

  const {
    actor,
    createdAt,
    canonical,
    duplicate,
    isCanonicalOfClosedDuplicate: isCanonicalDuplicate,
    databaseId,
    viewerCanUndo,
    pendingUndo,
    id,
  } = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment MarkedAsDuplicateEvent on MarkedAsDuplicateEvent {
        actor {
          avatarUrl(size: 64)
          login
          profileResourcePath
          __typename
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        createdAt
        duplicate {
          ... on Issue {
            ...IssueLink @dangerously_unaliased_fixme
            id
            number
          }
          ... on PullRequest {
            ...IssueLink @dangerously_unaliased_fixme
            id
            number
          }
        }
        canonical {
          ... on Issue {
            ...IssueLink @dangerously_unaliased_fixme
            id
            number
          }
          ... on PullRequest {
            ...IssueLink @dangerously_unaliased_fixme
            number
            id
          }
        }
        isCanonicalOfClosedDuplicate
        databaseId
        viewerCanUndo
        pendingUndo
        id
      }
    `,
    queryRef,
  )

  const canonicalId = canonical?.id

  const unMarkAsDuplicate = useCallback(() => {
    if (!canonicalId) return

    const input = {duplicateId: currentIssueId, cannonicalId: canonicalId}
    commitUnmarkIssueAsDuplicateMutation({environment, input, eventId: id})
  }, [canonicalId, environment, currentIssueId, id])

  const highlighted = String(databaseId) === highlightedEventId

  if (!duplicate) return <></>

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={DuplicateIcon}
      fillRow={viewerCanUndo}
    >
      <TimelineRow.Main>
        {isCanonicalDuplicate ? (
          <>
            marked <IssueLink inline data={duplicate} targetRepositoryId={repositoryId} targetOwnerLogin={ownerLogin} />{' '}
            as a duplicate of this issue{' '}
          </>
        ) : (
          <>
            {LABELS.timeline.markedAsDuplicate}
            <>
              &nbsp;
              <IssueLink inline data={duplicate} targetRepositoryId={repositoryId} targetOwnerLogin={ownerLogin} />{' '}
            </>
          </>
        )}
      </TimelineRow.Main>
      {viewerCanUndo && !pendingUndo && duplicate?.number && !isCanonicalDuplicate ? (
        <TimelineRow.Trailing>
          <Button onClick={unMarkAsDuplicate} aria-label={LABELS.undoMarkIssueAsDuplicate(duplicate.number)}>
            Undo
          </Button>
        </TimelineRow.Trailing>
      ) : null}
    </TimelineRow>
  )
}
